/**
 * DeliveryTab — deliverables setup question, deliverable cards, and delivery overview table
 */
import { C, RATING_SCALE, getRatingColor, getRatingLabel } from '../../data/managerReviewData';
import type { Employee, Deliverable, FeedbackItem } from '../../types/managerReview';
import DeliverableCard from './DeliverableCard';

const CULTURE_AMP_URL = 'https://finn.eu.cultureamp.com/app/auth?redirect=https%3A%2F%2Ffinn.eu.cultureamp.com%2Fapp%2Fhome';

interface DeliveryTabProps {
  employee: Employee;
  level: string;
  deliverables: Deliverable[];
  setDeliverables: (fn: (d: Deliverable[]) => Deliverable[]) => void;
  deliveryRating: number | null;
  setDeliveryRating: (r: number | null) => void;
  helpAnswer: 'yes' | 'no' | null;
  setHelpAnswer: (v: 'yes' | 'no') => void;
  selfReview: string;
  feedbackItems: FeedbackItem[];
  perfFeedbackRaw?: string;
  anytimeFeedbackRaw?: string;
}

export default function DeliveryTab({
  employee,
  level,
  deliverables,
  setDeliverables,
  deliveryRating,
  setDeliveryRating,
  helpAnswer,
  setHelpAnswer,
  selfReview,
  feedbackItems,
  perfFeedbackRaw,
  anytimeFeedbackRaw,
}: DeliveryTabProps) {
  const add = () => setDeliverables(d => [...d, { title: '', rating: null, competencies: [], wellDone: '', improvement: '' }]);
  const upd = (i: number, v: Deliverable) => setDeliverables(d => d.map((x, j) => j === i ? v : x));
  const rm = (i: number) => setDeliverables(d => d.filter((_, j) => j !== i));

  const allDeliverableRated = deliverables.length > 0 && deliverables.every(d => d.rating !== null);

  return (
    <div style={{ paddingTop: 8, display: 'flex', gap: 24, alignItems: 'flex-start' }}>
      {/* ── Left column ── */}
      <div style={{ flex: 1, minWidth: 0 }}>
      {/* Deliverables setup question */}
      <div style={{
        background: '#fafafa', borderRadius: 10, padding: 24,
        boxShadow: '0 2px 10px rgba(0,0,0,0.07)', border: `1px solid ${C.border}`,
        marginBottom: 20,
      }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: C.text, marginBottom: 12 }}>
          Did you set Deliverables for {employee.name.split(' ')[0]} ahead of this review?
        </div>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <button
            onClick={() => setHelpAnswer('yes')}
            style={{
              padding: '8px 18px', borderRadius: 7,
              border: `1.5px solid ${helpAnswer === 'yes' ? '#26bd59' : C.border}`,
              background: helpAnswer === 'yes' ? '#26bd5918' : '#fff',
              color: helpAnswer === 'yes' ? '#26bd59' : '#555',
              fontFamily: 'Inter', fontSize: 13, fontWeight: 600, cursor: 'pointer', transition: 'all 0.15s',
            }}
          >✓ Yes, I set them in CultureAmp</button>
          <button
            onClick={() => setHelpAnswer('no')}
            style={{
              padding: '8px 18px', borderRadius: 7,
              border: `1.5px solid ${helpAnswer === 'no' ? C.blue : C.border}`,
              background: helpAnswer === 'no' ? '#0087eb18' : '#fff',
              color: helpAnswer === 'no' ? C.blue : '#555',
              fontFamily: 'Inter', fontSize: 13, fontWeight: 600, cursor: 'pointer', transition: 'all 0.15s',
            }}
          >✗ No, I didn't set any</button>
        </div>

        {helpAnswer === 'yes' && (
          <div style={{ marginTop: 14, padding: '14px 16px', background: 'rgba(38,189,89,0.07)', borderRadius: 8, borderLeft: `3px solid ${C.green}` }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: C.text, marginBottom: 4 }}>🎉 Thank you for driving change at FINN!</div>
            <div style={{ fontSize: 13, color: C.muted, lineHeight: 1.6, marginBottom: 12 }}>
              The Talent Framework is new and not every people leader has started using it yet — the fact that you set Deliverables upfront makes a real difference.
            </div>
            <div style={{ borderTop: `1px solid ${C.border}`, paddingTop: 10 }}>
              <div style={{ fontSize: 13, color: C.muted, lineHeight: 1.65, marginBottom: 8 }}>
                Add the deliverables below — keep them close to what you set in CultureAmp.
              </div>
              <a
                href={CULTURE_AMP_URL}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 4,
                  fontSize: 12, color: C.muted, fontWeight: 600, textDecoration: 'none',
                  padding: '4px 10px', borderRadius: 6, border: `1px solid ${C.border}`, background: '#fff',
                }}
              >Open CultureAmp ↗</a>
            </div>
          </div>
        )}

        {helpAnswer === 'no' && (
          <div style={{ marginTop: 14, padding: '14px 16px', background: '#f5f9ff', borderRadius: 8, borderLeft: '3px solid #0087eb' }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: C.blue, marginBottom: 8 }}>No worries — let's set them retrospectively now</div>
            <div style={{ fontSize: 12, color: '#444', lineHeight: 1.7, marginBottom: 14 }}>
              The Talent Framework is new and needs some time to adapt. We will set Deliverables together retrospectively now to do an impact-focused performance assessment.
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {[
                { n: '1', title: 'Recall the key impact expected', body: 'Think back to what you expected this person to deliver over the last 6 months.' },
                { n: '2', title: 'Create deliverables based on actual business priorities', body: 'Add one deliverable per major impact area/result. Use the title field to describe it as if you had set it upfront.' },
                { n: '3', title: 'Rate each retrospectively', body: 'For each deliverable, ask: "Did they meet, exceed, or fall short of what I would have expected here?"' },
              ].map(s => (
                <div key={s.n} style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                  <div style={{
                    width: 22, height: 22, borderRadius: '50%', background: C.blue, color: '#fff',
                    fontSize: 11, fontWeight: 800, display: 'flex', alignItems: 'center',
                    justifyContent: 'center', flexShrink: 0, marginTop: 1,
                  }}>{s.n}</div>
                  <div>
                    <div style={{ fontSize: 12, fontWeight: 700, color: '#333', marginBottom: 2 }}>{s.title}</div>
                    <div style={{ fontSize: 12, color: '#666', lineHeight: 1.6 }}>{s.body}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {helpAnswer !== null && (
        <>
          {deliverables.map((d, i) => (
            <DeliverableCard
              key={i}
              index={i}
              id={`deliverable-${(d.title || '').toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '')}`}
              employee={employee}
              level={level}
              data={d}
              onChange={v => upd(i, v)}
              onRemove={() => rm(i)}
              selfReview={selfReview}
              feedbackItems={feedbackItems}
              perfFeedbackRaw={perfFeedbackRaw}
              anytimeFeedbackRaw={anytimeFeedbackRaw}
            />
          ))}
          {deliverables.length < 10 && (
            <button
              onClick={add}
              style={{
                width: '100%', padding: 14, border: '1.5px dashed rgba(255,255,255,0.45)',
                borderRadius: 10, background: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.75)',
                fontSize: 13, fontWeight: 600, cursor: 'pointer', marginBottom: 24, fontFamily: 'Inter',
                transition: 'border-color 0.15s, color 0.15s, background 0.15s',
              }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.7)'; e.currentTarget.style.color = '#fff'; e.currentTarget.style.background = 'rgba(255,255,255,0.10)'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.45)'; e.currentTarget.style.color = 'rgba(255,255,255,0.75)'; e.currentTarget.style.background = 'rgba(255,255,255,0.06)'; }}
            >+ Add Deliverable</button>
          )}
        </>
      )}

      </div>{/* end left column */}

      {/* ── Right column: sticky Overall Delivery Rating ── */}
      <div style={{ width: 420, flexShrink: 0, position: 'sticky', top: 24, alignSelf: 'flex-start' }}>
        <div style={{
          background: '#fff', borderRadius: 10, border: `1px solid ${C.border}`,
          boxShadow: '0 2px 12px rgba(0,0,0,0.06)', overflow: 'hidden',
        }}>
          <div style={{ padding: '12px 16px', borderBottom: '1px solid #f0f0f0', background: '#fafafa' }}>
            <div style={{ fontWeight: 700, fontSize: 13, color: '#0f0f0f' }}>Overall Delivery Rating</div>
          </div>
          <div style={{ padding: '12px 16px' }}>

            {/* Nudge — appears once all deliverables are rated but overall not yet set */}
            {allDeliverableRated && !deliveryRating && (
              <div style={{
                marginBottom: 12, padding: '9px 12px',
                background: '#f0f7ff', border: '1.5px solid #0087eb', borderRadius: 7,
                fontSize: 12, fontWeight: 600, color: '#0087eb',
                display: 'flex', alignItems: 'flex-start', gap: 7, lineHeight: 1.4,
              }}>
                <span>↓</span>
                <span>All deliverables rated — now set your overall delivery rating.</span>
              </div>
            )}

            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <tbody>
                {deliverables.map((d, i) => (
                  <tr key={i} style={{ borderBottom: '1px solid #f5f5f5' }}>
                    <td style={{ padding: '7px 0', lineHeight: 1.4, paddingRight: 8 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <div style={{
                          width: 18, height: 18, borderRadius: 5, background: '#ebebeb', color: '#999',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          fontSize: 9, fontWeight: 800, flexShrink: 0,
                        }}>{i + 1}</div>
                        <span style={{ fontSize: 12, color: '#666', fontWeight: 500 }}>
                          {d.title || `Deliverable ${i + 1}`}
                        </span>
                      </div>
                    </td>
                    <td style={{ textAlign: 'right', whiteSpace: 'nowrap', paddingLeft: 4 }}>
                      {d.rating ? (
                        <span style={{
                          display: 'inline-block', padding: '2px 8px', borderRadius: 5,
                          background: getRatingColor(d.rating) + '18', border: `1.5px solid ${getRatingColor(d.rating)}`,
                          color: getRatingColor(d.rating), fontSize: 11, fontWeight: 700,
                        }}>
                          {d.rating} – {getRatingLabel(d.rating)}
                        </span>
                      ) : <span style={{ fontSize: 11, color: '#ccc' }}>—</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Overall rating card — two states: active / done */}
            {(() => {
              const isDone = !!deliveryRating;
              const cardBorder = isDone ? '#26bd59' : '#0087eb';
              const cardBg = isDone ? '#f0faf4' : '#f0f7ff';
              const badgeBg = isDone ? '#26bd59' : '#0087eb';
              const badgeText = isDone ? '✓ Done' : 'Required · Your judgment call';
              return (
                <div style={{ marginTop: 14, padding: '12px 14px', background: cardBg, borderRadius: 8, border: `1.5px solid ${cardBorder}` }}>
                  <div style={{
                    display: 'inline-flex', alignItems: 'center',
                    background: badgeBg, color: '#fff',
                    fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 4,
                    letterSpacing: '0.04em', marginBottom: 8,
                  }}>{badgeText}</div>
                  <div style={{ fontSize: 12, fontWeight: 700, color: '#1a1a1a', marginBottom: 4 }}>
                    Overall Delivery Rating
                  </div>
                  <div style={{ fontSize: 11, color: '#666', lineHeight: 1.5, marginBottom: 10 }}>
                    Not necessarily the average — deliverables may carry different weight.
                  </div>
                  <select
                    value={deliveryRating || ''}
                    onChange={e => setDeliveryRating(e.target.value ? Number(e.target.value) : null)}
                    style={{
                      width: '100%', padding: '7px 10px', borderRadius: 7,
                      border: `1.5px solid ${deliveryRating ? getRatingColor(deliveryRating) : C.border}`,
                      fontSize: 12, fontFamily: 'Inter', fontWeight: 600,
                      color: deliveryRating ? getRatingColor(deliveryRating) : C.muted,
                      background: deliveryRating ? getRatingColor(deliveryRating) + '18' : '#fff',
                      outline: 'none', cursor: 'pointer',
                    }}
                  >
                    <option value=''>Select…</option>
                    {RATING_SCALE.map(r => <option key={r.v} value={r.v}>{r.v} – {r.label}</option>)}
                  </select>
                  <div style={{ marginTop: 8, fontSize: 11, color: '#888', display: 'flex', alignItems: 'flex-start', gap: 5, lineHeight: 1.5 }}>
                    <span style={{ flexShrink: 0 }}>ℹ</span>
                    <span>This rating will be shared with the employee in their review conversation.</span>
                  </div>
                </div>
              );
            })()}
          </div>
        </div>
      </div>

    </div>
  );
}
